import React, { Dispatch, ReactNode, SetStateAction } from 'react';
import { Controller } from 'react-hook-form';
import { PencilIcon, ReserveIcon } from '../../../../../../../../Components/IconComponents';
import moment from 'moment';
import useTableReservation from './tableReserve-hook';
import { useNoAuthHooks } from '../../../../../../../../Hooks/hooks';
import { ButtonTypes, ICartItem, ItemCounterTypes } from '../../../../../../../../Interfaces/componentTypes';
import { ITableReserveForm } from '../../../../../../../../Interfaces/formValue-interfaces';
import { formatPercent } from '../../../../../../../../Utilits/utilits';
import { IBusinessProfile } from '../../../../../../../../Interfaces/responseTypes';
import { VisitorPageContents } from '../../../../../visitorPage';
import AuthInput from '../../../../../../../../Components/Inputs/AuthInput/authInput';
import TelInput from '../../../../../../../../Components/Inputs/TelInput/telInput';
import CustomTimePicker from '../../../../../../../../Components/Inputs/TimePicker/timePicker';
import useLanguagesHooks from '../../../../../../../../Components/Languages/setLanguage-hooks';
import Button from '../../../../../../../../Components/Button/button';
import CustomDialog from '../../../../../../../../Components/Dialog/customDialog';
import NoteDialog from '../NoteDialog/noteDialog';
import { RedPlus, SmallTrash } from '../../../../../../../../Assets/Icons';
import ItemCounter from '../../ItemCounter/itemCounter';
import NoData from '../../../../../../../../Components/NoData/noData';
import ToggleSwitch from '../../../../../../../../Components/ToggleSwitch/toggleSwitch';
import OrderPaymentSection from '../../OrderPayment/orderPaymentSection';
import OrderScheduleNotices from '../../OrderScheduleNotices/orderScheduleNotices';
import styles from './tableReserve.module.scss';

export interface IReserveOrderProps {
    companyId: number,
    company_name: string,
    company_logo: string,
    company_contacts?: string[],
    cart: ICartItem[],
    currencySymbol: string,
    start_time?: string | null,
    end_time?: string | null,
    business_profile?: IBusinessProfile | null,
    serviceFee?: number,
    pos_terminal?: boolean | null,
    isReserveForm: ITableReserveForm | null
    onCloseDialog: () => void;
    setDialog: Dispatch<SetStateAction<ReactNode>>;
    setIsReserveForm: Dispatch<SetStateAction<ITableReserveForm | null>>,
    setContentType: Dispatch<SetStateAction<VisitorPageContents>>,
    setMainCart: Dispatch<SetStateAction<[] | ICartItem[]>>
}

const TableReserveDialog: React.FC<IReserveOrderProps> = (props) => {
    const { start_time, end_time, currencySymbol, serviceFee, business_profile } = props;
    const { t, activeLanguage } = useLanguagesHooks();
    const { baseURL, renderOptions } = useNoAuthHooks();

    const {
        open,
        errors,
        control,
        dialogRef,
        showOrder,
        dialogCart,
        totalPrice,
        dineInPrice,
        showEmptyCart,
        totalItemCount,
        newOrderLoading,
        onOrder,
        register,
        onGoToMenu,
        getValues,
        watch,
        handleNote,
        handleSubmit,
        onOpenNoteDialog,
        onCloseNoteDialog,
        removeFromDialogCart,
        onCloseReserveDialog,
        handleOptionCountChange,
    } = useTableReservation(props);
    
    const selectedDate = watch('date');

    return (
        <div className={styles.container} ref={dialogRef}>
            <div className={styles.topSection}>
                <OrderScheduleNotices profile={business_profile} selectedDate={selectedDate} />
                <div className={styles.header}>
                    <ReserveIcon width={30} height={30} color='var(--primary-hover)' />
                    <p> {t('Visitor.Reserve.Header')}</p>
                </div>
            </div>
            <div className={styles.form}>
                <TelInput
                    name='phone_number'
                    control={control}
                    label={t('Visitor.Reserve.Phone')}
                    showTextError={false}
                    style={styles.tellInput}
                    required={true}
                    error={errors.phone_number}
                />
                <AuthInput
                    register={register}
                    registerName='persons_count'
                    label={t('Visitor.Reserve.Persons')}
                    showTextError={false}
                    required={true}
                    type='number'
                    error={errors.persons_count}
                />
                <div className={styles.dateTimeBox}>
                    <p className={styles.label}>
                        {t('Visitor.Reserve.Date')}
                        {(errors.time || errors.date)
                            && <span style={{ color: '#b00020' }}> *</span>
                        }
                    </p>
                    <div className={styles.dateTimeInputRow}>
                        <AuthInput
                            register={register}
                            registerName='date'
                            type='date'
                            showTextError={false}
                            style={styles.datePicker}
                            inputBoxStyles={styles.datePickerInput}
                            defaultValue={selectedDate}
                            minDate={moment().format('YYYY-MM-DD')}
                            required={true}
                            error={errors.date}
                        />
                        <Controller
                            name="time"
                            control={control}
                            rules={{ required: true }}
                            render={({ field, fieldState }) => (
                                <CustomTimePicker
                                    value={field.value!}
                                    onChange={field.onChange}
                                    style={styles.timePicker}
                                    showTextError={false}
                                    placeholder=""
                                    error={errors.time}
                                    startTime={start_time}
                                    endTime={end_time}
                                    selectedDate={selectedDate}
                                    businessProfile={business_profile}
                                />
                            )}
                        />

                    </div>
                </div>
                <div className={styles.noteContainer}>
                    <div className={styles.noteLabel}>
                        {t('Visitor.Delivery.Notes')}
                    </div>
                    <div className={styles.noteBox} onClick={onOpenNoteDialog}>
                        <div className={styles.noteText}>
                            <p >
                                {getValues('notes') && getValues('notes')?.length! > 30
                                    ? `${getValues('notes')?.slice(0, 30)}...`
                                    : getValues('notes')}
                            </p>
                        </div>
                        <PencilIcon color={`var(--input-label)`} />
                    </div>
                </div>
            </div>
            <OrderPaymentSection pos_terminal={props.pos_terminal} control={control} />
            <div className={styles.cartItem}>
                <div className={styles.cartLabel}>
                    <p>{t('Visitor.Reserve.Order_Type')}</p>
                </div>
                <div className={styles.switcherRow}>
                    <p>{t('Visitor.Reserve.InPlace')}</p>
                    <ToggleSwitch control={control} name='dine_in' />
                </div>
                <div className={styles.switcherRow}>
                    <p>{t('Visitor.Reserve.PreOrder')}</p>
                    <ToggleSwitch control={control} name='pre_order' />
                </div>
                {
                    showOrder &&
                    <div
                        className={styles.cartBox}
                    >
                        {dialogCart.length > 0 ? (
                            dialogCart.map((item) => {
                                const localizedTitle = (item[`name_${activeLanguage}` as keyof ICartItem] as string) || item.name_en;
                                return (
                                    <div className={styles.item} key={item.id}>
                                        <div className={styles.itemHeader}>
                                            <div className={styles.titleBox}>
                                                <img src={`${baseURL}${item.image}`} className={styles.image} alt='image.png' />
                                                <p className={styles.title}>{localizedTitle}</p>
                                            </div>
                                            <div className={styles.deleteIcon} onClick={() => removeFromDialogCart(item.id)}>
                                                <img src={SmallTrash} alt='trash.svg' />
                                            </div>
                                        </div>
                                        <div className={styles.options}>
                                            {item.option.map((opt) => (
                                                <div className={styles.option} key={opt.option_id}>
                                                    <p className={styles.portion}>{renderOptions(opt)}</p>
                                                    <div className={styles.price}>
                                                        <p>{parseInt(opt.price)}</p>
                                                        <p>{currencySymbol}</p>
                                                    </div>
                                                    <ItemCounter
                                                        count={opt.count}
                                                        option={opt}
                                                        type={ItemCounterTypes.MAIN_CART}
                                                        handleAddOption={(_, c) =>
                                                            handleOptionCountChange(item.id, opt.option_id, c)
                                                        }
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            showEmptyCart && (
                                <div className={styles.noMenuBox}>
                                    <NoData
                                        withButton
                                        onPressButton={onGoToMenu}
                                        message={t('Visitor.Empty_Cart')}
                                        containerStyle={styles.noDataContainer}
                                        imageStyle={styles.noDataImage}
                                        textStyle={styles.noDataText}
                                        buttonTitle={t('Visitor.Reserve.Go_Menu')}
                                        buttonStyle={styles.noDataButton}
                                        buttonTitleStyle={styles.noDataButtonTitle}
                                    />
                                </div>
                            )
                        )}
                    </div>
                }
                {
                    dialogCart.length > 0 && showOrder
                    &&
                    <div className={styles.goMenuRow} >
                        <div className={styles.goMenuBox} onClick={onGoToMenu}>
                            <img src={RedPlus} alt='add.svg' />
                            <p>{t('My_Business.Menu')}</p>
                        </div>
                    </div>

                }
            </div>
            {
                dialogCart.length > 0 && showOrder
                &&
                <div className={styles.totalBox} >
                    <div className={styles.totalInfoBox}>
                        <div className={styles.totaInfoRow}>
                            <p className={styles.totalInfoTitle}>
                                {t('Visitor.Delivery.Order')}
                            </p>
                            <div className={styles.totalItemsInfoRow}>
                                <p className={styles.totalItemsCount}>
                                    {`${totalItemCount} ${totalItemCount > 1 ? t('Visitor.Delivery.Items') : t('Visitor.Delivery.Item')}`}
                                </p>
                                <p className={styles.totalCost}>
                                    {`${totalPrice}${currencySymbol}`}
                                </p>
                            </div>
                        </div>
                        {
                            serviceFee &&
                            <div className={styles.totaInfoRow}>
                                <p className={styles.totalInfoTitle}>
                                    {t('Visitor.Service_Fee')}
                                </p>
                                <p className={styles.totalCost}>
                                    {`${formatPercent(String(serviceFee))}%`}
                                </p>
                            </div>
                        }
                    </div>
                    <div className={styles.totaInfoRow}>
                        <p className={styles.totalDineTitle}>
                            {t('Visitor.Delivery.Total')}
                        </p>
                        <p className={styles.totalDineCost}>
                            {`${parseInt(String(dineInPrice))}${currencySymbol}`}
                        </p>
                    </div>
                </div>
            }
            <div className={styles.buttonsRow}>
                <Button
                    buttonType={ButtonTypes.Secondary}
                    title={t('Buttons.Cancel')}
                    buttonStyle={styles.cancelButton}
                    buttonTitleStyle={styles.buttontitle}
                    onClick={onCloseReserveDialog}
                />
                <Button
                    buttonType={ButtonTypes.Primery}
                    title={t('Visitor.Reserve.Order')}
                    buttonStyle={styles.orderButton}
                    buttonTitleStyle={styles.buttontitle}
                    isLoading={newOrderLoading}
                    onClick={handleSubmit(onOrder)}
                />
            </div>
            <CustomDialog
                open={open}
                onClose={onCloseNoteDialog}
                children={
                    <NoteDialog
                        note={getValues('notes')}
                        onHandleNote={handleNote} />}
            />
        </div>
    )
}

export default TableReserveDialog
